import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  AiManagementCard,
  AiManagementCardMember,
  AiManagementState,
} from 'store/modules/ai_management_store/types';
import { updateCardsMembers } from 'services/actions/ai_management_cards';
import AiManagementAvatar from 'components/atoms/ai_management_avatar';
import AiManagementModalHeader from 'components/molecules/ai_management_modal_header';
import { Container } from 'components/molecules/ai_management_members_modal/styles';

interface AiManagementMembersBulkModalProps {
  cards: AiManagementCard[];
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const AiManagementMembersBulkModal: React.FC<AiManagementMembersBulkModalProps> = ({
  cards,
  show,
  onClose,
  onSaved,
}) => {
  const team = useSelector<ApplicationState, AiManagementState['team']>(
    (state) => state.session.ai_management.team,
  );
  const [search, setSearch] = useState('');
  const [addIds, setAddIds] = useState<number[]>([]);
  const [removeIds, setRemoveIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSearch('');
    setAddIds([]);
    setRemoveIds([]);
  }, [show]);

  const currentMembers = cards.reduce<AiManagementCardMember[]>((acc, card) => {
    card.members.forEach((member) => {
      if (!acc.some((item) => item.id === member.id)) acc.push(member);
    });
    return acc;
  }, []);

  const staged = team
    .filter((member) => addIds.includes(member.id))
    .map((member) => ({ id: member.id, name: member.name, initials: member.initials }));

  const biddingMembers = [
    ...currentMembers.filter((member) => !removeIds.includes(member.id)),
    ...staged.filter((member) => !currentMembers.some((item) => item.id === member.id)),
  ];

  const stageAdd = (id: number) => {
    setRemoveIds((ids) => ids.filter((item) => item !== id));
    setAddIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  const stageRemove = (id: number) => {
    setAddIds((ids) => ids.filter((item) => item !== id));
    if (currentMembers.some((member) => member.id === id)) {
      setRemoveIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
    }
  };

  const handleSave = () => {
    if (!addIds.length && !removeIds.length) {
      onClose();
      return;
    }

    setSaving(true);
    updateCardsMembers({
      card_ids: cards.map((card) => card.id),
      add_contact_client_ids: addIds,
      remove_contact_client_ids: removeIds,
    })
      .then((res) => {
        if (!res || res.status >= 400) return;
        onSaved();
        onClose();
      })
      .finally(() => setSaving(false));
  };

  const query = search.trim().toLowerCase();
  const visibleMembers = biddingMembers.filter((member) =>
    member.name.toLowerCase().includes(query),
  );
  const teamMembers = team.filter(
    (member) =>
      !biddingMembers.some((item) => item.id === member.id) &&
      member.name.toLowerCase().includes(query),
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <Container>
        <AiManagementModalHeader title="Alterar membros" onClose={onClose} />

        <div className="members-body p-4">
          <div className="members-input-box d-flex align-items-center pr-2 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar membros"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-link p-0 text-primary flex-shrink-0"
              onClick={() => setSearch('')}
            >
              Limpar
            </button>
          </div>

          <div className="font-weight-bold mb-2">Membros da licitação</div>
          {visibleMembers.length === 0 && (
            <div className="text-muted small mb-2">Nenhum membro.</div>
          )}
          {visibleMembers.map((member) => (
            <div key={member.id} className="d-flex align-items-center py-2">
              <AiManagementAvatar
                initials={member.initials}
                memberId={member.id}
                size={32}
                className="mr-2"
              />
              <span className="flex-grow-1">{member.name}</span>
              <button
                type="button"
                className="btn btn-link p-0 text-muted d-flex align-items-center"
                aria-label={`Remover ${member.name}`}
                onClick={() => stageRemove(member.id)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          ))}

          <div className="font-weight-bold mb-2 mt-3">Membros da equipe</div>
          {teamMembers.length === 0 && (
            <div className="text-muted small">Nenhum contato disponível.</div>
          )}
          {teamMembers.map((member) => (
            <button
              key={member.id}
              type="button"
              className="members-team-row d-flex align-items-center"
              onClick={() => stageAdd(member.id)}
            >
              <AiManagementAvatar
                initials={member.initials}
                memberId={member.id}
                size={32}
                className="mr-2"
              />
              <span className="flex-grow-1 text-left">{member.name}</span>
            </button>
          ))}
        </div>

        <div className="members-footer d-flex justify-content-center border-top px-4 py-3">
          <button
            type="button"
            className="btn btn-primary px-4"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </Container>
    </Modal>
  );
};

export default AiManagementMembersBulkModal;
